import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { TooltipDirective, TooltipOptions } from 'ng2-tooltip-directive';
import { Subject } from 'rxjs';
import { FiTooltipService } from '@fi-shared/fi-tooltip/fi-tooltip.service';

export enum FiInfoIconMode {
    Common = 0,
    WithoutIconAbsolute = 1,
}

export type TTooltipTextAlign = 'center' | 'left' | 'right';
export type TFiInfoPosition = 'top' | 'right' | 'right-under' | 'left';

@Component({
    selector: 'fi-info-icon',
    template: `
        @if (!isTooltipShownFormStart) {
          <div
            id="toolfip"
            class="fi-info-icon-tooltip"
            [ngClass]="{
                'info-icon info_outlineCcmIcon': isIconTooltipShown,
                BrandedColor: isTooltipIconBrandedColor,
                maxSize: isMaxSize && !isIconTooltipShown,
            }"
            [tooltip]="tooltipContent"
            [options]="tooltipOptions"
            (mouseenter)="showTooltip()"
            (mouseleave)="hideTooltip()"
            (click)="toggleTooltip($event)"
          ></div>
        }
        
        @if (isTooltipShownFormStart) {
          <div
            class="fi-info-icon-tooltip"
            [class.maxSize]="isMaxSizeFromStart"
            [tooltip]="tooltipContent"
            [options]="tooltipOptions"
          ></div>
        }
        
        <ng-template #tooltipContent>
          <ng-content></ng-content>
        </ng-template>
        `,
    styleUrls: ['./info-icon.component.scss', '../fi-tooltip/fi-tooltip-content.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-info-icon' },
    standalone: false,
})
export class InfoIconComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() fiInfoPosition: TFiInfoPosition = 'left';
    @Input() fiInfoPositionMobile: string = 'right';
    @Input() isIconTooltipShown: boolean = true;
    @Input() isTooltipShownFormStart: boolean = false;
    @Input() isMaxSize: boolean = false;
    @Input() isMaxSizeFromStart: boolean = false;
    @Input() disableToggleByClick = false;
    @Input() mode = FiInfoIconMode.Common;
    @Input() tooltipClass: string | string[];
    @Input() autoPlacement: boolean = true;

    @Input() showTooltipExternalTriggerState: boolean = false;
    @Input() hideTooltipOnHoverInsideTooltip: boolean = false;

    @Input() isTooltipIconBrandedColor: boolean = true;
    @Input() tooltipTextAlignment: TTooltipTextAlign = 'left';
    @ViewChild(TooltipDirective, { static: false }) tooltipRef: TooltipDirective;
    tooltipOptions: TooltipOptions;
    isTooltipShown: boolean = false;

    private mouseenterListener: () => void;
    private mouseleaveListener: () => void;
    constructor(
        private detectDevices: DetectDevicesService,
        private renderer: Renderer2,
        private elementRef: ElementRef<HTMLElement>,
        private fiTooltipService: FiTooltipService,
    ) {}

    ngOnInit() {
        if (this.mode === FiInfoIconMode.WithoutIconAbsolute) {
            // todo implement separate directive
            this.isIconTooltipShown = false;
            // this.isTooltipShownFormStart = false;
            this.isMaxSize = true;
            this.disableToggleByClick = true;
            const nativeElement = this.elementRef.nativeElement;
            this.renderer.setStyle(nativeElement.parentElement, 'position', 'relative');
            this.renderer.setStyle(nativeElement, 'position', 'absolute');
            this.renderer.setStyle(nativeElement, 'left', '0px');
            this.renderer.setStyle(nativeElement, 'top', '0px');
            this.renderer.setStyle(nativeElement, 'height', '100%');
            this.renderer.setStyle(nativeElement, 'width', '100%');
        }

        this.tooltipOptions = this.getTooltipOptions();
        this.fiTooltipService.subscribeUpdatePositionOnMouseWheel(() => this.tooltipRef, this.ngOnDestroy$);
    }

    ngAfterViewInit() {
        if (this.isTooltipShownFormStart) {
            this.tooltipRef.show();
            this.isTooltipShown = true;
            requestAnimationFrame(() => {
                setTimeout(() => {
                    this.fiTooltipService.updateTooltipPosition(this.tooltipRef);
                }, 0);
            });
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.showTooltipExternalTriggerState)
            changes.showTooltipExternalTriggerState.currentValue ? this.showTooltip() : this.hideTooltip();
    }

    getTooltipOptions(): TooltipOptions {
        let placement = 'right';
        if (this.fiInfoPosition === 'right') {
            placement = 'left';
        } else if (this.fiInfoPosition === 'right-under') {
            placement = 'bottom';
        } else if (this.fiInfoPosition === 'top') {
            placement = this.fiInfoPosition;
        }
        if (this.detectDevices.isMobilePhone()) {
            placement = this.fiInfoPositionMobile;
        }

        let textAlignClass: string = 'text-align-' + this.tooltipTextAlignment;

        let tooltipClass = Array.isArray(this.tooltipClass) ? this.tooltipClass.join(' ') : this.tooltipClass;

        return <TooltipOptions>{
            placement: placement,
            'tooltip-class': `fi-info-icon-tooltip ${textAlignClass} ${tooltipClass}`,
            theme: 'light',
            'animation-duration': 200,
            'hide-delay-mobile': 0,
            'hide-delay': 0,
            trigger: 'none',
            contentType: 'template' as const,
            autoPlacement: this.autoPlacement,
        };
    }

    showTooltip() {
        this.tooltipRef.show();
        this.isTooltipShown = true;
        setTimeout(() => this.setEventsToTooltipBox());
    }

    hideTooltip() {
        if (this.tooltipRef) {
            this.tooltipRef.hide();
        }
        this.isTooltipShown = false;
    }

    setEventsToTooltipBox() {
        if (this.tooltipRef.componentRef && !this.hideTooltipOnHoverInsideTooltip) {
            this.removeMouseListeners();
            this.mouseenterListener = this.renderer.listen(
                this.tooltipRef.componentRef.instance.elementRef.nativeElement,
                'mouseenter',
                () => {
                    this.tooltipRef.show();
                    this.isTooltipShown = true;
                },
            );
            this.mouseleaveListener = this.renderer.listen(
                this.tooltipRef.componentRef.instance.elementRef.nativeElement,
                'mouseleave',
                () => {
                    this.hideTooltip();
                },
            );
        }
    }

    removeMouseListeners() {
        if (this.mouseleaveListener) {
            this.mouseleaveListener();
            this.mouseleaveListener = undefined;
        }
        if (this.mouseenterListener) {
            this.mouseenterListener();
            this.mouseenterListener = undefined;
        }
    }

    toggleTooltip(event?) {
        if (this.disableToggleByClick) return;

        if (event) {
            event.stopPropagation();
        }

        this.isTooltipShown ? this.hideTooltip() : this.showTooltip();
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.hideTooltip();
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
